/**
 * AdminTable — polished reusable data table with dynamic pagination
 *
 * Props:
 *   data          array    Full dataset (pagination is client-side)
 *   columns       array    [{ key, label, render?, className?, headerClass? }]
 *   loading       bool
 *   emptyTitle    string
 *   emptySubtitle string
 *   defaultPageSize number  (default 10)
 *   dataTestId    string
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox } from 'lucide-react';

/* ── Pagination engine ─────────────────────────────────────────── */
function buildPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 4) pages.push('…');
  const start = Math.max(2, current - 1);
  const end   = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 3) pages.push('…');
  pages.push(total);
  return pages;
}

/* ── Pagination bar ────────────────────────────────────────────── */
function PaginationBar({ page, totalPages, pageSize, totalItems, onPage, onPageSize }) {
  const from = Math.min((page - 1) * pageSize + 1, totalItems);
  const to   = Math.min(page * pageSize, totalItems);
  const pages = buildPages(page, totalPages);

  const BtnPage = ({ label, icon: Icon, disabled, onClick, active }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center transition-colors select-none"
      style={{
        height: '32px',
        minWidth: '32px',
        padding: '0 6px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: active ? 600 : 400,
        border: active ? 'none' : '1px solid #E5E7EB',
        backgroundColor: active ? '#1C1C1C' : disabled ? 'transparent' : '#FFFFFF',
        color: active ? '#FFFFFF' : disabled ? '#D1D5DB' : '#374151',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: active ? 'none' : disabled ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {Icon ? <Icon size={13} /> : label}
    </button>
  );

  return (
    <div
      className="flex flex-col sm:flex-row items-center gap-3 px-4 py-3"
      style={{ borderTop: '1px solid #F3F4F6', backgroundColor: '#FAFAFA' }}
    >
      {/* Left: showing info + per-page */}
      <div className="flex items-center gap-3 text-xs" style={{ color: '#6B7280' }}>
        <span>
          Showing <span style={{ fontWeight: 600, color: '#374151' }}>{totalItems === 0 ? 0 : from}–{to}</span> of <span style={{ fontWeight: 600, color: '#374151' }}>{totalItems}</span>
        </span>
        <div className="flex items-center gap-1.5">
          <span>Rows:</span>
          <Select value={String(pageSize)} onValueChange={v => onPageSize(Number(v))}>
            <SelectTrigger
              className="h-7 text-xs"
              style={{ width: '64px', borderColor: '#E5E7EB', backgroundColor: '#FFFFFF', borderRadius: '8px' }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}>
              {[10, 25, 50, 100].map(n => (
                <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: page buttons */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <BtnPage icon={ChevronsLeft}  disabled={page === 1}          onClick={() => onPage(1)}            />
          <BtnPage icon={ChevronLeft}   disabled={page === 1}          onClick={() => onPage(page - 1)}     />
          {pages.map((p, i) =>
            p === '…'
              ? <span key={`ellipsis-${i}`} className="px-1 text-xs" style={{ color: '#9CA3AF' }}>…</span>
              : <BtnPage key={p} label={p} active={p === page} onClick={() => onPage(p)} />
          )}
          <BtnPage icon={ChevronRight}  disabled={page === totalPages} onClick={() => onPage(page + 1)}     />
          <BtnPage icon={ChevronsRight} disabled={page === totalPages} onClick={() => onPage(totalPages)}   />
        </div>
      )}
    </div>
  );
}

/* ── Main DataTable ────────────────────────────────────────────── */
export default function AdminTable({
  data = [],
  columns = [],
  loading = false,
  emptyTitle = 'No data',
  emptySubtitle = '',
  defaultPageSize = 10,
  dataTestId,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Reset page on data change
  useEffect(() => { setPage(1); }, [data.length]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const rows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  const handlePageSize = (size) => { setPageSize(size); setPage(1); };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        border: '1px solid #E5E7EB',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
      data-testid={dataTestId}
    >
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          {/* Header */}
          <thead>
            <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {columns.map((col, ci) => (
                <th
                  key={col.key || ci}
                  className={col.headerClass || ''}
                  style={{
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #E5E7EB',
                    ...(col.width ? { width: col.width } : {}),
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  {columns.map((col, ci) => (
                    <td key={ci} style={{ padding: '12px 16px' }}>
                      <Skeleton className="h-4 w-full rounded" style={{ backgroundColor: '#F3F4F6' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                      style={{ backgroundColor: '#F3F4F6' }}
                    >
                      <Inbox size={20} style={{ color: '#9CA3AF' }} />
                    </div>
                    <p className="text-sm font-medium" style={{ color: '#374151' }}>{emptyTitle}</p>
                    {emptySubtitle && <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>{emptySubtitle}</p>}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={row.id || ri}
                  className="group"
                  style={{
                    borderBottom: ri < rows.length - 1 ? '1px solid #F3F4F6' : 'none',
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {columns.map((col, ci) => (
                    <td
                      key={ci}
                      className={col.className || ''}
                      style={{ padding: '10px 16px', verticalAlign: 'middle' }}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!loading && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={data.length}
          onPage={setPage}
          onPageSize={handlePageSize}
        />
      )}
    </div>
  );
}
